import { Observable, of, defer } from "rxjs"
import { switchMap, catchError, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { Router } from "../../../../server/router"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    CATEGORY_TABLE_ITEM_CLICKED,
    FETCH_CATEGORY_DETAIL_REQUESTED,
    fetchCategoryDetailRequested,
    fetchCategoryDetailSuccess,
} from "./actions"
import { createActionLoadingError } from "../../../data/actions"
import { getCategoryDetails } from "./selectors"
import { SEND_CSV_FILE_REQUESTED, sendCsvFileFailure, sendCsvFileSuccess } from "../../../../api/actions"
import { getHttpHeaders, getSelectedAccountId, getUserAccountId } from "../../../auth/selectors"
import { getCurrentPathQuery } from "../../../router/selectors"
import { fetchCategoryDetailApi } from "./detail/request"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { waitForAccountSelected } from "../../reports/customers/customersTable/epics"
import { prepareMenuImportUploadApi, uploadMenuImportFile } from "../../../../api/menuImport"
import { createEventRequest, getEventData, requestEvent } from "../../../../events/utils"
import { EventType } from "../../../../events/types"
import {
    MenuImportConfirmedPayload,
    MenuImportFailedPayload,
    MenuImportReport,
} from "../../../../events/MenuImport/types"
import { NotificationType, openNotificationWithModalDetail } from "../../../uiKit/Notification/Notification"

// Send cat CSV
export const sendCsvEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_CSV_FILE_REQUESTED),
        switchMap((action: Action) => {
            logger.info("sendCsvEpic - requested", {
                data: action.payload,
            })

            const headers = getHttpHeaders(deps.getState())
            const accountId = getSelectedAccountId(deps.getState())
            const requestedByUserAccountId = getUserAccountId(deps.getState())
            const correlationId = deps.idgen.next()

            if (!accountId) {
                return [sendCsvFileFailure("Account id is missing")]
            }

            return prepareMenuImportUploadApi(headers, {
                filename: action.payload.file.name,
                fileType: "text/csv",
                type: action.payload.type,
            }).pipe(
                switchMap(upload =>
                    uploadMenuImportFile(upload.signedRequest, action.payload.file, "text/csv").pipe(
                        switchMap(() =>
                            defer(() =>
                                requestEvent(
                                    headers,
                                    createEventRequest(
                                        EventType.MenuImportRequested,
                                        {
                                            accountId: accountId,
                                            correlationId: correlationId,
                                        },
                                        {
                                            importId: upload.importId,
                                            accountId: accountId,
                                            fileKey: upload.fileKey,
                                            type: action.payload.type,
                                            fileName: action.payload.file.name,
                                            requestedByUserAccountId: requestedByUserAccountId,
                                        }
                                    ),
                                    20_000
                                )
                            ).pipe(
                                switchMap(response => {
                                    const payload = response?.data as MenuImportConfirmedPayload | undefined
                                    const successMessage = payload?.report?.message ?? "Menu imported successfully"

                                    openNotificationWithModalDetail(
                                        NotificationType.Success,
                                        "Menu imported successfully",
                                        successMessage,
                                        payload?.report ? formatMenuImportReport(payload.report) : successMessage,
                                        10
                                    )

                                    return [sendCsvFileSuccess(successMessage)]
                                }),
                                catchError(error =>
                                    defer(() => getEventData(headers, correlationId)).pipe(
                                        switchMap(eventData => {
                                            const payload = eventData?.data as MenuImportFailedPayload | undefined
                                            const errorMessage = payload?.message ?? getMenuImportErrorMessage(error)

                                            openNotificationWithModalDetail(
                                                NotificationType.Error,
                                                "Menu import failed",
                                                errorMessage,
                                                payload?.report ? formatMenuImportReport(payload.report) : errorMessage,
                                                20
                                            )

                                            return [sendCsvFileFailure(errorMessage)]
                                        }),
                                        catchError(() => {
                                            const errorMessage = getMenuImportErrorMessage(error)

                                            openNotificationWithModalDetail(
                                                NotificationType.Error,
                                                "Menu import failed",
                                                errorMessage,
                                                errorMessage,
                                                20
                                            )

                                            return [sendCsvFileFailure(errorMessage)]
                                        })
                                    )
                                )
                            )
                        )
                    )
                ),
                catchError(error => {
                    const errorMessage = getMenuImportErrorMessage(error)

                    openNotificationWithModalDetail(
                        NotificationType.Error,
                        "Menu import failed",
                        errorMessage,
                        errorMessage,
                        20
                    )

                    return [sendCsvFileFailure(errorMessage)]
                })
            )
        })
    )

export const categoryTableItemClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CATEGORY_TABLE_ITEM_CLICKED),
        switchMap((action: Action) => {
            Router.push(`/menu/categories/edit/${action.payload.id}`, undefined, { shallow: true })
            return [fetchCategoryDetailRequested()]
        })
    )

export const fetchCategoryDetailRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_CATEGORY_DETAIL_REQUESTED),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            const path = getCurrentPathQuery(deps.getState())

            if (!path) {
                return []
            }

            const { id } = path

            if (!id) {
                return []
            }

            // If we have detail already fetched, do not request again
            const details = getCategoryDetails(deps.getState())

            if (details.find(detail => detail.data.id === id)) {
                return []
            }

            return defer(() =>
                fetchCategoryDetailApi(getHttpHeaders(deps.getState()), {
                    categoryId: id,
                })
            ).pipe(
                switchMap(res => {
                    if (res) {
                        return [fetchCategoryDetailSuccess(res)]
                    }

                    return []
                }),
                catchError(err => of(createActionLoadingError(err)))
            )
        })
    )

export default [fetchCategoryDetailRequestedEpics, categoryTableItemClickedEpics, sendCsvEpic]

const formatMenuImportReport = (report: MenuImportReport): string =>
    [
        report.message,
        "",
        `Rows processed: ${report.counts.rowsProcessed}`,
        `Categories created: ${report.counts.categoriesCreated}`,
        `Products created: ${report.counts.productsCreated}`,
        `Modifiers created: ${report.counts.modifiersCreated}`,
        `Rows failed: ${report.counts.rowsFailed}`,
    ].join("\n")

const getMenuImportErrorMessage = (error: any): string => {
    if (typeof error?.body?.message === "string") {
        return error.body.message
    }

    if (typeof error?.body === "string") {
        return error.body
    }

    if (typeof error?.message === "string") {
        return error.message
    }

    if (typeof error === "string") {
        return error
    }

    return "Problem appeared during importing, please try again."
}
